class CartCrossSellCard extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.selectedOptions = [];
    this.currentVariant = null;
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}} €';
    this.defaultImage = null;
  }

  connectedCallback() {
    try {
      this.variants = JSON.parse(this.dataset.variants || '[]');
    } catch (error) {
      console.error(`[CartCrossSellCard] Failed to parse variants JSON for product ID ${this.dataset.productId}:`, error);
      this.variants = [];
    }

    this.defaultImage = this.dataset.defaultImage || '';

    const isMobileWithSelect = this.classList.contains('ccs-mobile-card') && this.querySelector('.ccs-mobile-card__select');
    if (isMobileWithSelect) {
      this.currentVariant = null;
      this.selectedOptions = [];
      this.updateMobileButton(null);
    } else if (this.variants.length > 0) {
      const firstVariant = this.variants.find(v => v.available) || this.variants[0];
      this.currentVariant = firstVariant;
      this.selectedOptions = [
        firstVariant.option1,
        firstVariant.option2,
        firstVariant.option3
      ].filter(Boolean);
    }

    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('.ccs-card__option-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleOptionClick(e));
    });

    const variantSelect = this.querySelector('.ccs-mobile-card__select');
    if (variantSelect) {
      variantSelect.addEventListener('change', (e) => this.handleSelectChange(e));
    }

    const atcBtn = this.querySelector('.ccs-card__atc-btn');
    if (atcBtn) {
      atcBtn.addEventListener('click', (e) => this.handleAddToCart(e));
    }

    const mobileAtcBtn = this.querySelector('.ccs-mobile-card__atc-btn');
    if (mobileAtcBtn) {
      mobileAtcBtn.addEventListener('click', (e) => this.handleAddToCart(e));
    }
  }

  handleSelectChange(e) {
    const select = e.currentTarget;
    select.classList.remove('is-error');
    const value = select.value;
    if (value === '') {
      this.currentVariant = null;
      this.setMobilePriceToFirstVariant();
      this.updateMobileButton(null);
      return;
    }
    const variantId = parseInt(value, 10);
    const variant = this.variants.find(v => v.id === variantId);
    if (variant) {
      this.currentVariant = variant;
      this.updateMobilePrice(variant);
      this.updateMobileButton(variant);
      this.updateMobileImage(variant);
    }
  }

  updateMobilePrice(variant) {
    const priceEl = this.querySelector('.ccs-mobile-card__price');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(variant.price);
      priceEl.dataset.price = variant.price;
    }
  }

  updateMobileButton(variant) {
    const btn = this.querySelector('.ccs-mobile-card__atc-btn');
    if (btn) {
      if (variant) {
        btn.dataset.variantId = variant.id;
        btn.disabled = !variant.available;
        btn.textContent = variant.available ? btn.dataset.addToCart : btn.dataset.soldOut;
      } else {
        btn.removeAttribute('data-variant-id');
        btn.dataset.variantId = '';
        btn.disabled = false;
        btn.textContent = btn.dataset.addToCart;
      }
    }
  }

  setMobilePriceToFirstVariant() {
    if (this.variants.length === 0) return;
    const first = this.variants.find(v => v.available) || this.variants[0];
    const priceEl = this.querySelector('.ccs-mobile-card__price');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(first.price);
      priceEl.dataset.price = first.price;
    }
  }

  updateMobileImage(variant) {
    const imgEl = this.querySelector('.ccs-mobile-card__image');
    if (imgEl) {
      const imageUrl = variant.featured_image?.src || this.defaultImage;
      if (imageUrl) {
        imgEl.src = this.getImageUrl(imageUrl, 160);
      }
    }
  }

  handleOptionClick(e) {
    const btn = e.currentTarget;
    const optionIndex = parseInt(btn.dataset.optionIndex, 10);
    const value = btn.dataset.value;

    this.selectedOptions[optionIndex] = value;

    const optionContainer = btn.closest('.ccs-card__option');
    optionContainer.querySelectorAll('.ccs-card__option-btn').forEach(b => {
      b.classList.remove('is-active');
    });
    btn.classList.add('is-active');

    this.updateVariant();
  }

  updateVariant() {
    const variant = this.variants.find(v => {
      return (
        (!v.option1 || v.option1 === this.selectedOptions[0]) &&
        (!v.option2 || v.option2 === this.selectedOptions[1]) &&
        (!v.option3 || v.option3 === this.selectedOptions[2])
      );
    });

    if (variant) {
      this.currentVariant = variant;
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateImage(variant);
    }
  }

  updatePrice(variant) {
    const priceEl = this.querySelector('.ccs-card__price');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(variant.price);
      priceEl.dataset.price = variant.price;
    }

    const unitPriceEl = this.querySelector('.ccs-card__unit-price');
    if (unitPriceEl && variant.unit_price_measurement) {
      const refValue = variant.unit_price_measurement.reference_value !== 1 
        ? variant.unit_price_measurement.reference_value 
        : '';
      unitPriceEl.textContent = `(${this.formatMoney(variant.unit_price)} / ${refValue}${variant.unit_price_measurement.reference_unit})`;
    }
  }

  updateButton(variant) {
    const btn = this.querySelector('.ccs-card__atc-btn');
    if (btn) {
      btn.dataset.variantId = variant.id;
      btn.disabled = !variant.available;
      
      if (variant.available) {
        btn.textContent = btn.dataset.addToCart;
      } else {
        btn.textContent = btn.dataset.soldOut;
      }
    }
  }

  updateImage(variant) {
    const imgEl = this.querySelector('.ccs-card__image');
    if (imgEl) {
      const imageUrl = variant.featured_image?.src || this.defaultImage;
      if (imageUrl) {
        imgEl.src = this.getImageUrl(imageUrl, 400);
        imgEl.srcset = `${this.getImageUrl(imageUrl, 400)} 1x, ${this.getImageUrl(imageUrl, 800)} 2x`;
      }
    }
  }

  getImageUrl(url, size) {
    if (!url) return '';
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d+)?(\.(jpg|jpeg|png|gif|webp))/, `_${size}x$2`);
    }
    return url;
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    const value = (cents / 100).toFixed(2);
    const formatted = value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const moneyFormat = this.dataset.moneyFormat;
    return moneyFormat
      .replace('{{amount}}', formatted)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', formatted);
  }

  async handleAddToCart(e) {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;

    if (!variantId || btn.disabled) {
      const mobileSelect = this.querySelector('.ccs-mobile-card__select');
      if (mobileSelect) {
        mobileSelect.classList.add('is-error');
        mobileSelect.focus();
      }
      return;
    }

    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `HTTP ${response.status}`);
      }

      await this.refreshCart();

      setTimeout(() => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }, 400);

    } catch (error) {
      console.error(`[CartCrossSellCard] Failed to add variant ${variantId} to cart for product ID ${this.dataset.productId}:`, error);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const pageContainer = document.getElementById('PageContainer');
      const cartAction = pageContainer ? pageContainer.dataset.cartAction : 'drawer';

      if (window.wetheme) {
        if (cartAction === 'drawer') {
          window.wetheme.toggleRightDrawer('cart', true, { cart: cart });
        } else {
          window.wetheme.updateCartDrawer(cart);
        }
      }
    } catch (error) {
      console.error('[CartCrossSellCard] Failed to fetch and refresh cart drawer after adding item:', error);
    }
  }
}

if (!customElements.get('cart-cross-sell-card')) {
  customElements.define('cart-cross-sell-card', CartCrossSellCard);
}
